import Navigation from "@/components/Navigation";
import Breadcrumbs from "@/components/Breadcrumbs";
import Footer from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Shirt, Palette, TrendingUp, Calendar, ShoppingCart, Building2, Factory, Database, Package, Truck } from "lucide-react";
import { Link } from "react-router-dom";
import { useSEO } from "@/hooks/useSEO";
import RelatedSolutionsSection from "@/components/RelatedSolutionsSection";

const Fashion = () => {
  useSEO({
    title: 'Fashion & Apparel Commerce Solutions | Ommni',
    description: 'Manage fashion product variants, seasonal collections, and visual merchandising with Ommni. Built for fashion brands and retailers with complex variant management needs.',
    keywords: 'fashion commerce, apparel management, fashion retail, variant management, clothing inventory',
    canonical: 'https://ommni.io/solutions/fashion',
  });

  const features = [
    {
      icon: Palette,
      title: "Complex Variant Management",
      description: "Effortlessly manage product variants across sizes, colors, styles, and materials with smart organization.",
    },
    {
      icon: Calendar,
      title: "Seasonal Collection Control",
      description: "Plan and execute seasonal launches with automated product lifecycle management.",
    },
    {
      icon: TrendingUp,
      title: "Style Performance Analytics",
      description: "Track which styles, colors, and sizes perform best to optimize your inventory mix.",
    },
    {
      icon: Shirt,
      title: "Rich Product Presentations",
      description: "Maintain beautiful product imagery and detailed specifications across all retail channels.",
    },
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Fashion & Apparel Commerce Solutions",
    "description": "Master the complexity of fashion retail with intelligent variant management and omnichannel excellence. Manage fashion product variants, seasonal collections, and visual merchandising.",
    "serviceType": "Fashion Retail Technology",
    "provider": {
      "@type": "Organization",
      "name": "Ommni",
      "url": "https://ommni.io"
    },
    "areaServed": {
      "@type": "Place",
      "name": "Global"
    },
    "url": "https://ommni.io/solutions/fashion"
  };

  return (
    <div className="min-h-screen">
      <Navigation />
      <main className="pt-32 px-4 pb-24">
        {/* JSON-LD Structured Data for Service */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
        />
        
        <div className="container mx-auto max-w-7xl">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-primary/10 mb-6">
              <Shirt className="h-10 w-10 text-primary" />
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Fashion & Apparel Solutions
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
              Master the complexity of fashion retail with intelligent variant management and omnichannel excellence
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button variant="cta" size="lg" asChild>
                <Link to="/contact">Get Started</Link>
              </Button>
              <Button variant="outline" size="lg" asChild>
                <Link to="/pricing">View Pricing</Link>
              </Button>
            </div>
          </div>

          {/* Features Grid */}
          <div className="grid md:grid-cols-2 gap-8 mb-16">
            {features.map((feature, index) => (
              <Card key={index} className="p-8 gradient-card">
                <div className="mb-4">
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                    <feature.icon className="h-6 w-6 text-primary" />
                  </div>
                </div>
                <h3 className="text-2xl font-bold mb-3">{feature.title}</h3>
                <p className="text-muted-foreground">{feature.description}</p>
              </Card>
            ))}
          </div>

          {/* Powered By Section */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-center mb-4">
              Powered By Ommni Features
            </h2>
            <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
              Fashion-specific solutions built on our core platform capabilities
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              <Card className="p-6 hover:shadow-lg transition-all hover:border-primary/50">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                  <Database className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-lg font-bold mb-2">
                  <Link to="/features/pim" className="hover:text-primary transition-colors">
                    Product Information Management
                  </Link>
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Manage complex fashion variants across sizes, colors, and styles with our powerful PIM system.
                </p>
              </Card>
              <Card className="p-6 hover:shadow-lg transition-all hover:border-primary/50">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                  <Package className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-lg font-bold mb-2">
                  <Link to="/features/inventory" className="hover:text-primary transition-colors">
                    Inventory Management
                  </Link>
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Track inventory by size and color in real-time across all your retail locations.
                </p>
              </Card>
              <Card className="p-6 hover:shadow-lg transition-all hover:border-primary/50">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                  <Truck className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-lg font-bold mb-2">
                  <Link to="/features/dom" className="hover:text-primary transition-colors">
                    Order Management
                  </Link>
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Fulfill fashion orders from stores, warehouses, or drop-shippers intelligently.
                </p>
              </Card>
            </div>
          </div>

          {/* Related Solutions */}
          <RelatedSolutionsSection
            solutions={[
              {
                icon: ShoppingCart,
                title: "E-commerce & Retail",
                description: "Multi-channel retail operations with real-time synchronization.",
                link: "/solutions/ecommerce",
                gradient: "bg-gradient-to-br from-blue-500/20 to-cyan-500/20",
              },
              {
                icon: Building2,
                title: "Wholesale & Distribution",
                description: "B2B operations with tiered pricing and bulk ordering.",
                link: "/solutions/wholesale",
                gradient: "bg-gradient-to-br from-purple-500/20 to-pink-500/20",
              },
              {
                icon: Factory,
                title: "Manufacturing",
                description: "Production to distribution with BOM management.",
                link: "/solutions/manufacturing",
                gradient: "bg-gradient-to-br from-orange-500/20 to-red-500/20",
              },
            ]}
          />

          {/* Benefits Section */}
          <div className="relative overflow-hidden rounded-lg border-2 bg-card p-12">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-purple-500/5 to-background"></div>
            <div className="relative z-10">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4">
                  Perfect for Fashion Brands
                </h2>
                <p className="text-muted-foreground max-w-2xl mx-auto">
                  From fast fashion to luxury brands, handle the unique challenges of apparel retail
                </p>
              </div>
              <div className="grid md:grid-cols-3 gap-8 text-center">
                <div>
                  <div className="text-4xl font-bold text-primary mb-2">90%</div>
                  <p className="text-muted-foreground">Less Time Managing Variants</p>
                </div>
                <div>
                  <div className="text-4xl font-bold text-primary mb-2">100+</div>
                  <p className="text-muted-foreground">Variants Per Product</p>
                </div>
                <div>
                  <div className="text-4xl font-bold text-primary mb-2">Real-Time</div>
                  <p className="text-muted-foreground">Inventory by Size & Color</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Breadcrumbs />
      <Footer />
    </div>
  );
};

export default Fashion;
